import { Position } from 'reactflow';
import type { FieldMapping, TransformationStep } from '../../services/api';
import type { CanvasNode, CanvasNodeData, CanvasNodeKind } from '../types';
import {
  CANVAS_GROUP_BODY_PADDING,
  CANVAS_GROUP_HEADER_HEIGHT,
  CANVAS_GROUP_WIDTH,
  CANVAS_INTERMEDIATE_COLUMN_X,
  CANVAS_LINK_BRIDGE_HORIZONTAL_JITTER,
  CANVAS_LINK_BRIDGE_MIDPOINT_SHIFT,
  CANVAS_LINK_BRIDGE_VERTICAL_JITTER,
  CANVAS_LINK_BRIDGE_VERTICAL_SCALE,
  CANVAS_ROW_SPACING,
  CANVAS_SOURCE_GROUP_X,
  CANVAS_TARGET_GROUP_X,
  CANVAS_TRANSFORMATION_COLUMN_STEP,
  CANVAS_TRANSFORMATION_COLUMN_X,
  CANVAS_TRANSFORMATION_MAX_DECAY_STEPS,
  CANVAS_TRANSFORMATION_STEP_DECAY,
  CANVAS_TRANSFORMATION_STEP_MIN,
} from '../layoutConstants';
import { stableHash } from './edgeStyling';
import { normaliseQualifiedValue, parseQualifiedName } from './qualifiedNames';
import { addMappingTemplatesToNode } from './transformAnalysis';
import type { GroupLayout, NodeRegistry } from './types';

export const createBaseNode = (
  id: string,
  kind: CanvasNodeKind,
  position: { x: number; y: number },
  data: Omit<CanvasNodeData, 'kind'>,
  extras: Partial<CanvasNode> = {}
): CanvasNode => ({
  id,
  type: kind,
  position,
  draggable: true,
  connectable: true,
  selectable: true,
  data: {
    ...data,
    ruleTemplates: data.ruleTemplates ?? [],
    kind,
  },
  ...extras,
});

export const ensureFieldNode = (
  registry: NodeRegistry,
  nodes: CanvasNode[],
  qualified: string,
  kind: CanvasNodeKind,
  group: GroupLayout | undefined,
  orderIndex: number,
  mapping: FieldMapping | undefined,
  aiGenerated?: boolean,
  confidence?: number,
  explicitPosition?: { x: number; y: number }
): CanvasNode => {
  const canonicalQualified = normaliseQualifiedValue(qualified);

  // Create unique key per mapping ONLY for intermediate nodes to allow distinct paths
  // For source/target schema nodes, we want standardized singletons (Fan-in/Fan-out)
  const isSchemaNode = kind === 'sourceField' || kind === 'targetField';

  // Phase 4: Overlap Fix. Force lowercase key for schema nodes to merge "Subject" and "SUBJECT".
  const registryKey = (mapping && !isSchemaNode)
    ? `${canonicalQualified}::${mapping.mapping_id}`
    : (isSchemaNode ? canonicalQualified.toLowerCase() : canonicalQualified);

  const targetRegistry =
    kind === 'targetField' ? registry.target : kind === 'sourceField' ? registry.source : registry.intermediate;
  const existing = targetRegistry.get(registryKey);
  if (existing) {
    // Still update metadata if node exists (shouldn't happen with unique keys)
    if (mapping && !existing.data.mappingIds.includes(mapping.mapping_id)) {
      existing.data.mappingIds = [...existing.data.mappingIds, mapping.mapping_id];
    }
    if (typeof confidence === 'number' && (!existing.data.confidence || confidence > existing.data.confidence)) {
      existing.data.confidence = confidence;
    }
    if (aiGenerated !== undefined) {
      existing.data.aiGenerated = aiGenerated;
    }
    addMappingTemplatesToNode(existing, mapping);
    return existing;
  }

  const parsedName = parseQualifiedName(canonicalQualified);
  let table = parsedName.table.trim();
  let field = parsedName.field.trim();

  if (mapping) {
    if (kind === 'sourceField') {
      table = (mapping.source_file || table).trim();
    } else if (kind === 'targetField') {
      table = (mapping.target_table || table).trim();
    }
  }

  const label = field || (kind === 'sourceField' ? mapping?.source_field : mapping?.target_field) || parsedName.field || parsedName.qualified;
  const dataField = field || label;
  const dataTable = table || (kind === 'sourceField' ? mapping?.source_file : mapping?.target_table) || parsedName.table;

  // Use registryKey for unique node ID
  const nodeId = `${kind}:${registryKey}`;

  // Use React Flow parenting to ensure fields move with the group
  // Coordinates must be relative to the parent group [0, 0] top-left
  let position = explicitPosition;
  let parentNode: string | undefined;
  let extent: 'parent' | undefined;

  if (group) {
    parentNode = group.id;
    extent = 'parent';
    if (!position) {
      // Relative position inside the group
      position = {
        x: CANVAS_GROUP_BODY_PADDING,
        y: CANVAS_GROUP_HEADER_HEIGHT + CANVAS_GROUP_BODY_PADDING + orderIndex * CANVAS_ROW_SPACING,
      };
    } else {
      position = {
        x: (explicitPosition?.x ?? 0) - group.x,
        y: (explicitPosition?.y ?? 0) - group.y
      };
    }
  } else {
    // Fallback for detached nodes (shouldn't happen for valid source/target fields)
    position = explicitPosition ?? {
      x: kind === 'sourceField' ? CANVAS_SOURCE_GROUP_X + CANVAS_GROUP_WIDTH + 40 : CANVAS_TARGET_GROUP_X - 40,
      y: CANVAS_GROUP_HEADER_HEIGHT + CANVAS_GROUP_BODY_PADDING + orderIndex * CANVAS_ROW_SPACING,
    };
  }

  const extras: Partial<CanvasNode> = {
    sourcePosition: kind === 'sourceField' || kind === 'intermediateField' ? Position.Right : undefined,
    targetPosition: kind === 'targetField' || kind === 'intermediateField' ? Position.Left : undefined,
    parentNode,
    extent,
    style: {
      width: CANVAS_GROUP_WIDTH - CANVAS_GROUP_BODY_PADDING * 2,
      zIndex: 3,
    },
  };

  const node = createBaseNode(
    nodeId,
    kind,
    position,
    {
      label,
      qualifiedName: canonicalQualified,
      table: dataTable,
      field: dataField,
      mappingIds: mapping ? [mapping.mapping_id] : [],
      mapping,
      confidence,
      aiGenerated,
      groupRole: group?.role,
    },
    extras
  );
  addMappingTemplatesToNode(node, mapping);

  targetRegistry.set(registryKey, node);
  nodes.push(node);
  return node;
};

export const resolveAbsolutePosition = (node: CanvasNode, nodes: CanvasNode[]): { x: number; y: number } => {
  if (!node.parentNode) {
    return node.position;
  }
  const parent = nodes.find((n) => n.id === node.parentNode);
  if (!parent) {
    return node.position;
  }
  return {
    x: parent.position.x + node.position.x,
    y: parent.position.y + node.position.y,
  };
};

export const ensureLinkBridgeNode = (
  registry: NodeRegistry,
  nodes: CanvasNode[],
  mapping: FieldMapping,
  sourceNode: CanvasNode,
  targetNode: CanvasNode,
  sourceIndex: number,
  targetIndex: number
): CanvasNode => {
  const nodeId = `link:${mapping.mapping_id}`;
  const existing = registry.bridge.get(nodeId);
  if (existing) {
    return existing;
  }

  const sourcePos = resolveAbsolutePosition(sourceNode, nodes);
  const targetPos = resolveAbsolutePosition(targetNode, nodes);

  const sourceY = sourcePos.y;
  const targetY = targetPos.y;
  const centerY = sourceY + (targetY - sourceY) / 2;
  const verticalSpan = Math.abs(targetY - sourceY);
  const hash = stableHash(mapping.mapping_id);
  const jitterBucket = (hash % 9) - 4; // range [-4,4]
  const diff = sourceIndex - targetIndex;
  const baseYOffset = diff * (CANVAS_ROW_SPACING * CANVAS_LINK_BRIDGE_VERTICAL_SCALE);
  const yJitter =
    baseYOffset + jitterBucket * Math.min(CANVAS_LINK_BRIDGE_VERTICAL_JITTER, Math.max(verticalSpan * 0.18, 10));
  const xBucket = ((hash >> 3) % 11) - 5;
  const xJitter = xBucket * CANVAS_LINK_BRIDGE_HORIZONTAL_JITTER;
  const midX = sourcePos.x + (targetPos.x - sourcePos.x) * CANVAS_LINK_BRIDGE_MIDPOINT_SHIFT;
  const node = createBaseNode(
    nodeId,
    'linkBridge',
    {
      x: midX + xJitter,
      y: centerY + yJitter,
    },
    {
      label: '',
      qualifiedName: mapping.mapping_id,
      table: mapping.target_table ?? '',
      field: mapping.target_field ?? '',
      mappingIds: [mapping.mapping_id],
      mapping,
      aiGenerated: mapping.transformation_steps?.some((step) => step?.ai_generated) ?? false,
      groupRole: undefined,
    },
    {
      selectable: false,
      draggable: true,
      sourcePosition: Position.Right,
      targetPosition: Position.Left,
      style: {
        width: 28,
        height: 28,
        zIndex: 2,
      },
    }
  );

  registry.bridge.set(nodeId, node);
  nodes.push(node);
  return node;
};

export const computeTransformationSpacing = (totalSteps: number): number => {
  if (totalSteps <= 1) {
    return CANVAS_TRANSFORMATION_COLUMN_STEP;
  }
  const decaySteps = Math.min(Math.max(totalSteps - 1, 0), CANVAS_TRANSFORMATION_MAX_DECAY_STEPS);
  const decayFactor = 1 - decaySteps * CANVAS_TRANSFORMATION_STEP_DECAY;
  const scaled = CANVAS_TRANSFORMATION_COLUMN_STEP * Math.max(decayFactor, 0.45);
  return Math.max(CANVAS_TRANSFORMATION_STEP_MIN, scaled);
};

export const ensureTransformationNode = (
  nodes: CanvasNode[],
  mapping: FieldMapping,
  step: TransformationStep,
  stepIndex: number,
  positionY: number,
  totalSteps: number,
  spacingOverride?: number
): CanvasNode => {
  const nodeId = `transformation:${mapping.mapping_id}:${step.step_id ?? stepIndex}`;
  const existing = nodes.find((candidate) => candidate.id === nodeId);
  if (existing) {
    return existing;
  }

  const { table, field } = parseQualifiedName(step.outputs?.[0] ?? mapping.target_field, mapping.target_table);
  const spacing = spacingOverride ?? computeTransformationSpacing(totalSteps);
  const x = CANVAS_TRANSFORMATION_COLUMN_X + stepIndex * spacing;
  const y = positionY;
  const node = createBaseNode(
    nodeId,
    'transformation',
    { x, y },
    {
      label: (typeof step.operation === 'string' && step.operation) || (typeof step.kind === 'string' && step.kind) || 'Transform',
      qualifiedName: `${table}.${field}`,
      table,
      field,
      mappingIds: [mapping.mapping_id],
      mapping,
      step,
      confidence: step.confidence ?? mapping.confidence,
      aiGenerated: step.ai_generated,
      metadata: {
        description: typeof step.description === 'string' ? step.description : undefined,
        expression: typeof step.expression === 'string' ? step.expression : undefined,
      },
    },
    {
      sourcePosition: Position.Right,
      targetPosition: Position.Left,
      style: {
        zIndex: 3,
        width: 208,
      },
    }
  );
  nodes.push(node);
  return node;
};
